import React, { useEffect, useRef, useState } from 'react';
import { Space } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import type { DateRange } from 'react-day-picker';
import { DatePicker, Tooltip } from '@hydrodigital/design-system';
import type { DashboardContextValueInterface } from '@/contexts/DashboardContext';
import { publishDashboardMapEvent } from '@/events/dashboardMapEvents';
import { showNavItem } from '../constants';
import TooltipLabel from './TooltipLabel';

export type DateRangeTuple = [Dayjs, Dayjs];

interface HeaderDatePickerProps {
	dates: DateRangeTuple | null;
	width: number;
	isSmallScreen: boolean;
	showCalendar: boolean;
	timeZone: number;
	pathname: string;
	setShowCalendar: (show: boolean) => void;
	setDates: (dates: DateRangeTuple) => void;
	setSelectedTime1: (time: Dayjs) => void;
	setSelectedTime2: (time: Dayjs) => void;
	setSelectionPhase: (phase: 'start' | 'end') => void;
	setContextValue: React.Dispatch<React.SetStateAction<DashboardContextValueInterface>>;
}

const HeaderDatePicker: React.FC<HeaderDatePickerProps> = ({
	dates,
	width,
	isSmallScreen,
	showCalendar,
	timeZone,
	pathname,
	setShowCalendar,
	setDates,
	setSelectedTime1,
	setSelectedTime2,
	setSelectionPhase,
	setContextValue,
}) => {
	const [initialShowCalendar, setInitialShowCalendar] = useState<boolean>(!showCalendar);
	const [datePickerOpen, setDatePickerOpen] = useState(false);
	const datePickerWrapperRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		setShowCalendar(initialShowCalendar);
	}, [initialShowCalendar]);

	useEffect(() => {
		if (!initialShowCalendar) {
			setDatePickerOpen(false);
		}
	}, [initialShowCalendar]);

	useEffect(() => {
		if (!initialShowCalendar || !datePickerOpen) {
			return;
		}

		const focusTimer = window.setTimeout(() => {
			const firstInput = datePickerWrapperRef.current?.querySelector('input');
			if (firstInput instanceof HTMLInputElement) {
				firstInput.focus();
				firstInput.select();
			}
		}, 0);

		return () => {
			window.clearTimeout(focusTimer);
		};
	}, [initialShowCalendar, datePickerOpen]);

	useEffect(() => {
		if (isSmallScreen) {
			setInitialShowCalendar(true);
		} else {
			setInitialShowCalendar(width > 1650);
		}
	}, [width, isSmallScreen]);

	const commitDateRange = (from: Dayjs, to: Dayjs) => {
		setSelectedTime1(from);
		setSelectedTime2(to);
		setDates([from, to]);
		setSelectionPhase('start');
		setContextValue((prev) => ({
			...prev,
			selectedDate: [from.format('YYYY-MM-DD HH:mm:ss'), to.format('YYYY-MM-DD HH:mm:ss')],
		}));
	};

	const applyDatePickerValue = (value: Date | Date[] | DateRange | undefined) => {
		if (!value || value instanceof Date || Array.isArray(value)) return;
		const from = value.from ? dayjs(value.from) : null;
		const to = value.to ? dayjs(value.to) : null;

		if (!from || !to) {
			return;
		}

		commitDateRange(from, to);
	};

	const datePickerDefaultValue = React.useMemo<DateRange>(() => ({
		from: dates?.[0] ? dates[0].toDate() : undefined,
		to: dates?.[1] ? dates[1].toDate() : undefined,
	}), [dates]);

	const datePickerResetKey = React.useMemo(
		() => `${dates?.[0]?.valueOf() ?? ''}-${dates?.[1]?.valueOf() ?? ''}`,
		[dates],
	);

	if (!showNavItem(import.meta.env.VITE_HEADER_SHOW_CALENDAR, pathname)) {
		return null;
	}

	return (
		<div
			style={{
				minWidth: 0,
				backgroundColor: 'white',
				borderRadius: '8px',
				padding: '0px 10px',
				margin: '10px 5px',
				zIndex: 10,
			}}
			className="flex items-center justify-center self-center"
		>
			<Space>
				<>
					<Tooltip position="down" minWidth={20} slot={<TooltipLabel height={16}>Calendar</TooltipLabel>}>
						<CalendarOutlined
							onClick={() => {
								setInitialShowCalendar((prev) => {
									const next = !prev;
									setDatePickerOpen(next);
									return next;
								});
							}}
							style={{
								color: '#74788D',
								fontSize: '20px',
								marginTop: '20px',
								marginLeft: '0px',
								alignSelf: 'center',
							}}
							rev={undefined}
						/>
					</Tooltip>
					{initialShowCalendar && (
						<div
							ref={datePickerWrapperRef}
							style={{ backgroundColor: 'white', borderRadius: 8, padding: '0 4px' }}
						>
							<DatePicker
								key={datePickerResetKey}
								advanced
								includeTime
								defaultValue={datePickerDefaultValue}
								open={datePickerOpen}
								onOpenChange={(open) => {
									if (open) {
										setDatePickerOpen(true);
									} else {
										setDatePickerOpen(false);
										setInitialShowCalendar(false);
										setShowCalendar(false);
									}
								}}
								keepOpenUntilApply
								label="Advanced Date Range Picker"
								mode="range"
								onAdvancedDropdownClickOutside={(_, event) => {
									const target = event?.target;
									if (
										target instanceof Node &&
										datePickerWrapperRef.current?.contains(target)
									) {
										return;
									}
									setDatePickerOpen(false);
									setInitialShowCalendar(false);
									setShowCalendar(false);
								}}
								onApply={(value) => {
									applyDatePickerValue(value);
									setDatePickerOpen(false);
									setInitialShowCalendar(true);
									setShowCalendar(true);
									publishDashboardMapEvent({ type: 'datesApplied' });
								}}
								onClear={() => {
									const userNow = dayjs().utcOffset(timeZone * 60);
									const from = userNow.subtract(24, 'hour');
									const to = userNow.add(24, 'hour');
									commitDateRange(from, to);
									setInitialShowCalendar(true);
									setShowCalendar(true);
								}}
								placeholder="Select date range"
								showPresets
							/>
						</div>
					)}
				</>
			</Space>
		</div>
	);
};

export default HeaderDatePicker;
